import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { CommissionCard, SearchView } from "../../../components";
import {
  getCommissions,
  postCommissionInvoiceUploadManuel,
  createCommission,
  getPayment,
  sendSingleInvoiceMail,
  getSingleInvoiceMailBody,
} from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import AuthStore from "../../../stores/AuthStore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const STATUS_OPTIONS = [
  { value: "Approved", label: "Onaylandı" },
  { value: "Change Requested", label: "Değişiklik İstendi" },
  { value: "Troubled", label: "Sorunlu" },
  { value: "Invoice Uploaded", label: "Fatura Yüklendi" },
  { value: "Mail Sended", label: "Mail Gönderildi" },
  { value: "Accounting", label: "Muhasebe" },
  { value: "Auto Created Invoice", label: "Otomatik Oluşturulan Fatura" },
  { value: "OCR Completed", label: "OCR Tamamlandı" },
  { value: "LUCA", label: "LUCA" },
  { value: "Paid", label: "Ödendi" },
  { value: "Admin Uploaded", label: "Admin Yükledi" },
  { value: "Not Paid", label: "Ödenmedi" },
  { value: "Return", label: "İade" },
];

export function CommissionListScreen() {
  const { isAdvancedTable } = useTableView();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [searchParams, setSearchParams] = useState({
    name: "",
    email: "",
    lucaId: "",
    status: "",
  });
  const [queryParams, setQueryParams] = useState({
    name: "",
    email: "",
    lucaId: "",
    status: "",
  });

  const [isPending, setIsPending] = useState(false);
  const [isCanUploadManuel, setIsCanUploadManual] = useState(false);
  const [isAddCommissionOpen, setIsAddCommissionOpen] = useState(false);
  const [newCommission, setNewCommission] = useState({
    influencerName: "",
    email: "",
    finalAmount: "",
  });

  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  // Get payment details to check if type is FIXED
  const { data: paymentData, isLoading: isPaymentLoading } = useQuery({
    queryKey: ["payment", id],
    queryFn: () => getPayment(id),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["commissions", id, page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getCommissions(id, {
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        sortColumn,
        sortDirection,
      }),
  });

  const isFixedPayment = paymentData?.data?.type === "FIXED";

  // Tek tek fatura talep maili gönderimi.
  // Mail gövdesi şirket türüne göre hazır gelir, gönderim öncesi düzenlenebilir.
  const [mailDialogCommission, setMailDialogCommission] = useState(null);
  const [mailBody, setMailBody] = useState("");
  const [isLoadingMailBody, setIsLoadingMailBody] = useState(false);

  const openMailDialog = async (commission) => {
    setMailDialogCommission(commission);
    setMailBody("");
    setIsLoadingMailBody(true);
    try {
      const response = await getSingleInvoiceMailBody({
        paymentId: id,
        commissionId: commission.id,
      });
      setMailBody(response?.data?.mailBody || "");
    } catch (error) {
      toast({
        title: "Hazır metin yüklenemedi",
        description:
          error?.response?.data?.error || "Metni elle yazabilirsiniz.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingMailBody(false);
    }
  };

  const sendMailMutation = useMutation({
    mutationFn: ({ commissionId, mailBodyMessage }) =>
      sendSingleInvoiceMail({ paymentId: id, commissionId, mailBodyMessage }),
    onSuccess: (response) => {
      setMailDialogCommission(null);
      toast({
        title: "Mail kuyruğa eklendi",
        description: response?.data?.message,
      });
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      queryClient.invalidateQueries({ queryKey: ["payment", id] });
    },
    onError: (error) => {
      toast({
        title: "Mail gönderilemedi",
        description:
          error?.response?.data?.error || "Bir şeyler ters gitti.",
        variant: "destructive",
      });
    },
  });

  const SendMailButton = ({ row }) => (
    <Button
      size="sm"
      variant="secondary"
      disabled={row.isMailSended || sendMailMutation.isPending}
      onClick={(e) => {
        e.stopPropagation();
        openMailDialog(row);
      }}
    >
      {row.isMailSended ? "Mail Gönderildi" : "Mail Gönder"}
    </Button>
  );

  const addCommissionMutation = useMutation({
    mutationFn: (data) => createCommission(id, data),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Komisyon başarıyla oluşturuldu",
      });
      setIsAddCommissionOpen(false);
      setNewCommission({
        influencerName: "",
        email: "",
        finalAmount: "",
      });
      queryClient.invalidateQueries(["commissions", id]);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.message,
      });
    },
  });

  const handleAddCommission = () => {
    if (
      !newCommission.influencerName ||
      !newCommission.email ||
      !newCommission.finalAmount
    ) {
      toast({
        variant: "destructive",
        title: "Doğrulama Hatası",
        description: "Tüm alanlar zorunludur",
      });
      return;
    }

    addCommissionMutation.mutate(newCommission);
  };

  const pageSettings = {
    title: "Komisyon Listesi",
    actions: [
      {
        name: "Geri",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => {
          navigate("/payments/" + id);
        },
      },
      {
        name: isCanUploadManuel
          ? "Manuel Upload Pdf DeActive"
          : "Manuel Upload Pdf Active",
        color: isCanUploadManuel
          ? "bg-red-100 hover:bg-red-400 hover:text-black text-red-800"
          : "bg-blue-100 hover:bg-blue-400 hover:text-black text-black-800",
        icon: "add-icon",
        onClick: () => {
          setIsCanUploadManual(!isCanUploadManuel);
        },
      },
      ...(AuthStore.isHavePermission("COMMISSION_CREATEMANUEL")
        ? [
            {
              name: "Komisyon Ekle",
              color: "bg-green-500 hover:bg-green-700 text-white",
              icon: "add-icon",
              onClick: () => {
                setIsAddCommissionOpen(true);
              },
            },
          ]
        : []),
    ],
  };

  const goToCommission = (commissionId) => () => {
    window.location.href = `/payments/${id}/commissions/${commissionId}`;
  };

  const uploadInvoice = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = {
      invoiceId: form.invoiceId.value,
      commissionId: form.commissionId.value,
      influencerName: form.influencerName.value,
      pdfFile: form.pdfFile.files[0],
    };
    const formDataAg = new FormData();
    formDataAg.append("fileName", data.influencerName);
    formDataAg.append("file", data.pdfFile);

    data.data = formDataAg;
    setIsPending(true);
    await postCommissionInvoiceUploadManuel(data).then(() => {
      setIsPending(false);
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
    });
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    { key: "influencerName", label: "Influencer Adı" },
    { key: "email", label: "E-posta" },
    { key: "finalAmount", label: "Net Tutar" },
    { key: "invoiceId", label: "Fatura No" },
    { key: "status", label: "Durum" },
    {
      key: "actions",
      label: "İşlemler",
      render: (row) => (
        <div
          className="flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2">
            <Button onClick={goToCommission(row.id)}>Detayları Gör</Button>
            <SendMailButton row={row} />
          </div>
          {isCanUploadManuel && (
            <form onSubmit={uploadInvoice}>
              <Input id="pdfFile" name="pdfFile" type="file" />
              <Button variant="secondary" type="submit">
                Fatura PDF'i Yükle
              </Button>
              <input type="hidden" name="invoiceId" value={row.invoiceId} />
              <input type="hidden" name="commissionId" value={row.id} />
              <input
                type="hidden"
                name="influencerName"
                value={row.influencerName}
              />
            </form>
          )}
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "influencerName", label: "Influencer Adı", sortable: true },
    { key: "email", label: "E-posta", sortable: true },
    { key: "totalEarnings", label: "Toplam Kazanç", sortable: true },
    { key: "ourCommission", label: "Komisyonumuz", sortable: true },
    { key: "finalAmount", label: "Net Tutar", sortable: true },
    { key: "invoiceId", label: "Fatura No", sortable: true },
    { key: "status", label: "Durum", sortable: true },
    { 
      key: "isMailSended", 
      label: "Mail Gönderildi", 
      sortable: true,
      accessor: (item) => item.isMailSended ? "Yes" : "No"
    },
    { key: "managerName", label: "Menajer", sortable: true },
    { key: "country", label: "Ülke", sortable: true },
    { 
      key: "minAcceptedInvoiceDate", 
      label: "En Erken Fatura Tarihi", 
      sortable: true,
      accessor: (item) => item.minAcceptedInvoiceDate ? new Date(item.minAcceptedInvoiceDate).toLocaleDateString() : "-"
    },
    { 
      key: "createdAt", 
      label: "Oluşturulma", 
      sortable: true,
      accessor: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"
    },
    {
      key: "actions",
      label: "İşlemler",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" onClick={goToCommission(row.id)}>Detayları Gör</Button>
          <SendMailButton row={row} />
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["influencerName", "email", "finalAmount", "status", "country", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading || isPending || isPaymentLoading}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={advancedColumns}
          columnVisibilityKey="commission-list-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${id}/commissions/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${id}/commissions/${item.id}`)}
          renderGridItem={(item) => (
            <div
              className={
                isCanUploadManuel ? "flex flex-col bg-yellow-100 p-2" : ""
              }
              key={item.id}
            >
              <CommissionCard
                email={item.email}
                finalAmount={item.finalAmount}
                invoiceId={item.invoiceId}
                influencerName={item.influencerName}
                status={item.status}
                onDetailClick={goToCommission(item.id)}
              />
              {isCanUploadManuel && (
                <form onSubmit={uploadInvoice}>
                  <div className="flex justify-center items-center mt-3">
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">Fatura PDF'i</Label>
                      <Input id="pdfFile" name="pdfFile" type="file" />
                      <Button variant="secondary" type="submit">
                        Fatura PDF'i Yükle
                      </Button>
                    </div>
                  </div>
                  <input type="hidden" name="invoiceId" value={item.invoiceId} />
                  <input type="hidden" name="commissionId" value={item.id} />
                  <input
                    type="hidden"
                    name="influencerName"
                    value={item.influencerName}
                  />
                </form>
              )}
            </div>
          )}
        />
      )}

      {/* Fatura talep maili - hazir metin duzenlenebilir */}
      <Dialog
        open={!!mailDialogCommission}
        onOpenChange={(open) => !open && setMailDialogCommission(null)}
      >
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fatura Talep Maili Gönder</DialogTitle>
            <DialogDescription>
              {mailDialogCommission
                ? `${mailDialogCommission.influencerName} (${mailDialogCommission.email})`
                : ""}
              {" — "}
              Metin şirket türüne göre hazır geldi, göndermeden önce
              düzenleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {isLoadingMailBody ? (
              <div className="py-8 text-center text-muted-foreground">
                Hazır metin yükleniyor...
              </div>
            ) : (
              <ReactQuill
                theme="snow"
                value={mailBody}
                onChange={setMailBody}
                className="bg-background"
              />
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMailDialogCommission(null)}
            >
              İptal
            </Button>
            <Button
              disabled={
                isLoadingMailBody ||
                sendMailMutation.isPending ||
                !mailBody?.trim()
              }
              onClick={() =>
                sendMailMutation.mutate({
                  commissionId: mailDialogCommission.id,
                  mailBodyMessage: mailBody,
                })
              }
            >
              {sendMailMutation.isPending ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddCommissionOpen} onOpenChange={setIsAddCommissionOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Yeni Komisyon Ekle</DialogTitle>
            <DialogDescription>
              Bu ödemeye yeni bir komisyon ekle
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="influencerName" className="text-right">
                Influencer Adı
              </Label>
              <Input
                id="influencerName"
                className="col-span-3"
                value={newCommission.influencerName}
                onChange={(e) =>
                  setNewCommission({
                    ...newCommission,
                    influencerName: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                E-posta
              </Label>
              <Input
                id="email"
                className="col-span-3"
                value={newCommission.email}
                onChange={(e) =>
                  setNewCommission({ ...newCommission, email: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="finalAmount" className="text-right">
                Net Tutar
              </Label>
              <Input
                id="finalAmount"
                className="col-span-3"
                type="number"
                step="0.01"
                value={newCommission.finalAmount}
                onChange={(e) =>
                  setNewCommission({
                    ...newCommission,
                    finalAmount: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAddCommission}
              disabled={addCommissionMutation.isPending}
            >
              {addCommissionMutation.isPending ? "Adding..." : "Add Commission"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
